<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12">
        <div v-if="success" class="card success-card">
          <div class="card-body green">You successfully added a new company!</div>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12">
        <b-card>
          <div slot="header">
            <strong>Register Company</strong>
          </div>
          <b-form @submit.prevent="registerCompany">
            <b-row v-if="admin.level === 0">
              <b-col sm="12">
                <b-form-group label="Organization" label-for="selectOrganization">
                  <b-form-select
                    id="selectOrganization"
                    v-model="organization"
                    v-validate="'required'"
                    :plain="true"
                    :options="organizations"
                    value="Please select"
                    name="organization"
                    :class="{'has-error': errors.has('organization')}"
                  >
                  </b-form-select>
                  <span class="red">{{ errors.first('organization') }}</span>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group>
              <label for="company_name">Company</label>
              <b-form-input
                type="text"
                v-validate="'required'"
                v-model="name"
                name="company_name"
                placeholder="Enter your company name"
                :class="{'has-error': errors.has('company_name')}"
              >
              </b-form-input>
              <span class="red">{{ errors.first('company_name') }}</span>
            </b-form-group>
            <b-row>
            <b-col sm="12">
              <b-form-group>
                <label class="typo__label">Assign Admins</label>
                <multiselect
                  v-model="selectedAdmins"
                  :options="admins"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :hide-selected="true"
                  :preserve-search="true"
                  placeholder=""
                  label="name"
                  track-by="name"
                  :preselect-first="true">
                  <template slot="tag" slot-scope="props">
                    <span class="custom__tag">
                      <span>{{ props.option.name }} ({{ props.option.email }})</span>
                      <span v-if="admin.admin_id !== props.option.id" class="custom__remove" @click="props.remove(props.option)">X</span>
                      <span v-else class="custom__remove__disabled" v-tooltip.top-center="msgRemove">X</span>
                    </span>
                  </template>
                </multiselect>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6">
              <b-form-group>
                <label for="street">Street</label>
                <b-form-input
                  type="text"
                  v-validate="'required'"
                  v-model="streetAddress"
                  placeholder="Enter street name"
                  name="street_address"
                  :class="{'has-error': errors.has('street_address')}"
                ></b-form-input>
                <span class="red">{{ errors.first('street_address') }}</span>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <label for="city">City</label>
                <b-form-input
                  type="text"
                  v-validate="'required'"
                  v-model="city"
                  placeholder="Enter your city"
                  name="city"
                  :class="{'has-error': errors.has('city')}"
                >
                </b-form-input>
                <span class="red">{{ errors.first('city') }}</span>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6">
              <b-form-group
                label="Province / State"
                label-for="selectProvinceState">
                <b-form-select id="selectProvinceState"
                  v-model="province"
                  v-validate="'required'"
                  :plain="true"
                  :options="stateProvince"
                  value="Please select"
                  name="state_province"
                  :class="{'has-error': errors.has('state_province')}"
                >
                </b-form-select>
                <span class="red">{{ errors.first('state_province') }}</span>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <label for="postal-code">Postal / Zip Code</label>
                <b-form-input
                  type="text"
                  v-validate="'required'"
                  v-model="postalCode"
                  placeholder="Postal / Zip Code"
                  name="postal_code"
                  :class="{'has-error': errors.has('postal_code')}"
                >
                </b-form-input>
                <span class="red">{{ errors.first('postal_code') }}</span>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="form-actions">
            <b-button type="submit" name="submit" variant="primary">Submit</b-button>
          </div>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import stateProvince from '../../stateProvince'

export default {
  name: 'Register',
  components: { Multiselect },
  data () {
    return {
      admin: {},
      admins: [],
      name: '',
      streetAddress: '',
      city: '',
      msgRemove: 'You cannot remove yourself',
      organization: '',
      organizations: [],
      province: '',
      postalCode: '',
      selectedAdmins: [],
      success: false,
      stateProvince
    }
  },
  methods: {
    /**
     * Assign an admin to a company by giving them full read permissions of the data. Note: this does not necessarily mean they have access to edit or delete the company. Only if they have level 1 access can they edit and delete.
     * @param {integer} companyId - The id of the company for the admin to be assigned to.
     * @param {integer} adminId - The id of the admin to be assigned for.
     */
    assignAdmin: function (companyId, adminId) {
      let self = this

      axios.post(`${process.env.VUE_APP_API_URL}api/admins/assign`, {
        admin_id: adminId,
        company_id: companyId
      })
        .then(function (response) {
          self.success = true
          window.scrollTo(0, 0)
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Gets the current authenticated admin and stores details in the admin data property object as well as the default admin that will be assigned to the company. The admin creating a company must be assigned to it unless another admin removes them. This keeps at least 1 admin assigned to a company at all times.
     */
    getAdmin: function () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/admins/${localStorage.getItem('user-id')}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          self.admin = response.data.data
          self.selectedAdmins = self.getAdmins([response.data.data])
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Formats the list of organization admins into a array of objects to be rendered in a bootstrap-vue table.
     * @param {array} data - The array of admins in the organization.
     */
    getAdmins (data) {
      let newData = []

      for (let i = 0; i < data.length; i++) {
        newData.push({
          id: data[i].admin_id,
          name: data[i].first_name + ' ' + data[i].last_name,
          email: data[i].email,
          level: data[i].level
        })
      }

      return newData
    },
    /**
     * Gets the list of admins associated with an organization from the backend API and stores it in the admins array data property.
     */
    getOrganizationAdmins () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/admins`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          self.admins = self.getAdmins(response.data.data)
          self.totalRows = self.admins.length
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Formats the list of organization data into a array of objects to be rendered in a select element.
     * @param {array} data - The array of organization data.
     */
    getOrganizationData (data) {
      let newData = []

      for (let i = 0; i < data.length; i++) {
        newData.push({
          value: data[i].org_id,
          text: data[i].name
        })
      }

      return newData
    },
    /**
     * Gets the list of organizations from the backend API.
     */
    getOrganizations () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/organizations`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          self.organizations = self.getOrganizationData(response.data.data)
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Submits the form information to the backend API once submit button is pressed. The information is passed through a validator to make sure they meet the required details. Once the data is submitted, the admins that were selected to be assigned to this company are sent to the assignAdmin method to be processed.
     */
    registerCompany: function () {
      let self = this

      const { name, streetAddress, city, postalCode, province } = this

      this.$validator.validateAll().then((result) => {
        if (result) {
          axios.post(`${process.env.VUE_APP_API_URL}api/companies`, {
            name,
            street_address: streetAddress,
            city,
            postal_code: postalCode,
            province
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-access-token': localStorage.getItem('user-token')
            }
          })
            .then(function (response) {
              for (let i = 0; i < self.selectedAdmins.length; i++) {
                if (self.selectedAdmins[i].id !== self.admin.admin_id) {
                  self.assignAdmin(response.data.company_id, self.selectedAdmins[i].id)
                }
              }

              self.success = true
            })
            .catch(function (error) {
              console.log(error.response)
              console.log(error)
            })
        }
      }).catch(() => {
        console.log(this.errors.all())
      })
    }
  },
  mounted () {
    this.getAdmin()
    this.getOrganizationAdmins()
    this.getOrganizations()
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
